import { Component } from '@angular/core';
import { TableConfig } from '@shared/interfaces/table/table-config';
import {
  AuthRoles,
  MeteringPointChargeInformationCollectionDto,
  MeteringPointChargeInformationDto,
  MeteringPointChargeInformationService
} from '@app/api/dmc';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { TableComponent } from '@shared/components/table/table.component';
import { MeteringPointPricingTable } from '@shared/table-configs/meteringpoint/meteringpoint-pricing.table';
import { HeaderService } from '@app/core/services/header.service';
import {
  meteringpointPricingActionMenu
} from '@views/meteringpoint/meteringpoint-pricing/meteringpoint-pricing.action-menu';
import { ModalService } from '@app/core/services/modal.service';
import {
  QueryMasterdataChargeComponent
} from '@shared/modals/meteringpoint/query-masterdata-charge/query-masterdata-charge.component';
import {
  AssociateChargeInformationComponent
} from '@shared/modals/meteringpoint/associate-charge-information/associate-charge-information.component';
import {
  EditAssoicatedChargeInformationComponent
} from '@shared/modals/meteringpoint/edit-assoicated-charge-information/edit-assoicated-charge-information.component';
import {
  DisassociateChargeInformationComponent
} from '@shared/modals/meteringpoint/disassociate-charge-information/disassociate-charge-information.component';
import { MenuAction } from '@shared/interfaces/header/menu-action';
import { hasRole } from '@app/core/guards/has-role.guard';

@Component({
  selector: 'app-meteringpoint-pricing',
  imports: [
    TableComponent,
  ],
  templateUrl: './meteringpoint-pricing.component.html',
  styleUrl: './meteringpoint-pricing.component.css'
})
export class MeteringpointPricingComponent {
  /** Actions **/
  public mpWriter = hasRole([AuthRoles.MeteringPointWriter]);
  public actions: MenuAction[] = [
    {
      ...meteringpointPricingActionMenu[0],
      callback: () => this.openAssociateModal(),
      disabled: () => !this.mpWriter
    },
    {
      ...meteringpointPricingActionMenu[1],
      callback: () => this.openEditModal(),
      disabled: () => !this.mpWriter || !this.isSingleConfirmedOwnedSubscriptionElementSelected(),
    },
    {
      ...meteringpointPricingActionMenu[2],
      callback: () => this.openCloseModal(),
      disabled: () => !this.mpWriter || !this.isSingleConfirmedOwnedElementSelected(),
    },
    {
      ...meteringpointPricingActionMenu[3],
      callback: () => this.openRequestInformationsModal(),
    }
  ];

  /** Selected Elements **/
  public selectedElements: any[] = [];

  /** Table Config **/
  public pricingTableConfig: TableConfig<MeteringPointChargeInformationDto> = {
    ...MeteringPointPricingTable,
    contextActions: [
      this.actions[1],
      this.actions[2],
    ],
    refreshable: true,
    refresh: () => this.fetchChargeInformation(),
  };

  /** Table Data **/
  public pricingTableData: MeteringPointChargeInformationDto[] = [];

  /** ID **/
  public id?: string;

  constructor(
    private readonly meteringPointChargeInformationService: MeteringPointChargeInformationService,
    private readonly route: ActivatedRoute,
    private readonly headerMenuService: HeaderService,
    private readonly modalService: ModalService,
  ) {
    this.route.parent?.paramMap.subscribe((q: ParamMap) => {
      this.id = q.get('id')!;

      this.headerMenuService.setActions(this.actions);

      this.fetchChargeInformation();
    });
  }

  private fetchChargeInformation(): void {
    this.meteringPointChargeInformationService.meteringPointChargeInformationQueryBySingleMeteringPointPost({meteringPointIdentification: this.id})
      .subscribe((data: MeteringPointChargeInformationCollectionDto) => {
        this.pricingTableData = data.results ?? [];
      });
  }

  private isSingleConfirmedOwnedSubscriptionElementSelected(): boolean {
    return this.isSingleConfirmedOwnedElementSelected() && this.selectedElements[0].chargeType === 'Subscription';
  }

  private isSingleConfirmedOwnedElementSelected(): boolean {
    return this.selectedElements.length === 1 &&
      this.selectedElements[0].dataHubStatus === 'Confirmed' &&
      this.selectedElements[0].ownership === true;
  }

  public openEditModal(): void {
    this.modalService.show({
      component: EditAssoicatedChargeInformationComponent,
      title: 'chargeInformation.actions.editChargeInformation',
      actions: [
        {
          type: 'primary',
          label: 'system.button.save',
          method: 'save'
        },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: { chargeInformation: this.selectedElements[0], identification: this.id }
    }).subscribe(() => this.fetchChargeInformation());
  }

  public openCloseModal(): void {
    this.modalService.show({
      component: DisassociateChargeInformationComponent,
      title: 'chargeInformation.actions.closeChargeInformation',
      actions: [
        {
          type: 'primary',
          label: 'common.stop',
          method: 'disassociate'
        },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: { chargeInformation: this.selectedElements[0], identification: this.id }
    }).subscribe(() => this.fetchChargeInformation());
  }

  public openAssociateModal(): void {
    this.modalService.show({
      component: AssociateChargeInformationComponent,
      title: 'common.add',
      actions: [
        {
          type: 'primary',
          label: 'system.button.create',
          method: 'create'
        },
        {type: 'secondary', label: 'system.button.cancel', closeOnClick: true},
      ],
      params: { identification: this.id }
    }).subscribe(() => this.fetchChargeInformation());
  }

  public openRequestInformationsModal(): void {
    this.modalService.show({
      component: QueryMasterdataChargeComponent,
      title: 'meteringpoint.pricing.requestMasterDataCharge',
      actions: [
        {
          type: 'primary',
          label: 'system.button.request',
          method: 'requestMasterData'
        },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: {
        identification: this.id
      }
    }).subscribe(() => this.fetchChargeInformation());
  }
}
